"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRefetchQueries = exports.QueryType = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const graphql_1 = require("graphql");
const gql_1 = require("../attributes/gql");
const get_catalogs_query_1 = require("../catalog/get-catalogs.query");
const measures_1 = require("../measures");
const product_1 = require("../product");
const get_products_query_1 = require("../product/gql/get-products.query");
const product_tree_1 = require("../product-tree");
const product_tree_node_1 = require("../product-tree-node");
const gql_2 = require("../values/gql");
const store_1 = require("../../../store");
const getOperationName = (doc) => { var _a, _b, _c; return ((_c = (_b = (_a = doc.definitions.find((def) => def.kind === graphql_1.Kind.OPERATION_DEFINITION)) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : null); };
var QueryType;
(function (QueryType) {
    QueryType[QueryType["StrategyAttributes"] = 0] = "StrategyAttributes";
    QueryType[QueryType["ProductAttributes"] = 1] = "ProductAttributes";
    QueryType[QueryType["ProductCounters"] = 2] = "ProductCounters";
    QueryType[QueryType["Products"] = 3] = "Products";
    QueryType[QueryType["ProductDetails"] = 4] = "ProductDetails";
    QueryType[QueryType["StrategyAttributeValues"] = 5] = "StrategyAttributeValues";
    QueryType[QueryType["ProductTree"] = 6] = "ProductTree";
})(QueryType || (exports.QueryType = QueryType = {}));
const QueryTypeToDocumentNodeMap = {
    [QueryType.StrategyAttributes]: [gql_1.GET_AGGREGATED_TEXT_ATTRIBUTES], /* GET_AGGREGATED_PRODUCT_MEASURE_UNITS */
    [QueryType.ProductAttributes]: [gql_1.GET_AGGREGATED_TEXT_ATTRIBUTES, measures_1.GET_AGGREGATED_PRODUCT_MEASURES],
    [QueryType.Products]: [get_products_query_1.GET_PRODUCTS],
    [QueryType.ProductDetails]: [product_1.GET_PRODUCT_DETAILS],
    [QueryType.StrategyAttributeValues]: [
        gql_2.GET_AGGREGATED_TEXT_ATTRIBUTE_VALUES,
        get_products_query_1.GET_PRODUCTS,
    ], /* GET_AGGREGATED_PRODUCT_MEASURE_UNITS */
    [QueryType.ProductCounters]: [gql_1.GET_AGGREGATED_TEXT_ATTRIBUTES, gql_2.GET_AGGREGATED_TEXT_ATTRIBUTE_VALUES],
    [QueryType.ProductTree]: [get_catalogs_query_1.GET_CATALOGS, product_tree_1.GET_PRODUCT_TREE, product_tree_node_1.GET_PRODUCT_TREE_NODE_WITH_ONE_CHILD_LEVEL],
};
var CacheEntity;
(function (CacheEntity) {
    CacheEntity["ProductTreeNode"] = "ProductTreeNode";
})(CacheEntity || (CacheEntity = {}));
var CacheEntityField;
(function (CacheEntityField) {
    CacheEntityField["Attribute"] = "productAggregatedLabelsNew";
    CacheEntityField["ProductMeasure"] = "productAggregateMeasures";
    CacheEntityField["ProductPresentations"] = "productPresentationsNew";
    CacheEntityField["StrategyValues"] = "labelValidation";
    CacheEntityField["Children"] = "children";
})(CacheEntityField || (CacheEntityField = {}));
const updateCache = (_catalogId, selectedTreeNodeId) => (cache) => {
    cache.evict({ id: `${CacheEntity.ProductTreeNode}:${selectedTreeNodeId}`, fieldName: CacheEntityField.Attribute });
    cache.evict({ id: `${CacheEntity.ProductTreeNode}:${selectedTreeNodeId}`, fieldName: CacheEntityField.ProductMeasure });
    cache.gc();
};
const getQueryList = (queries) => queries.flatMap((queryType) => QueryTypeToDocumentNodeMap[queryType]);
const useRefetchQueries = () => {
    const client = (0, react_2.useApolloClient)();
    const catalogId = (0, store_1.useSelectedCatalogId)();
    const refetchProductTree = (0, react_1.useCallback)(() => client.refetchQueries({
        include: getQueryList([QueryType.ProductTree]),
        updateCache(cache) {
            cache.evict({ id: `${CacheEntity.ProductTreeNode}` });
            cache.gc();
        },
    }), [client]);
    const refetchProductTreeNodes = (0, react_1.useCallback)((treeNodeIdsToUpdate) => client.refetchQueries({
        include: getQueryList([QueryType.ProductTree]),
        updateCache(cache) {
            treeNodeIdsToUpdate.forEach((productTreeNodeId) => {
                cache.evict({ id: `${CacheEntity.ProductTreeNode}:${productTreeNodeId}`, fieldName: CacheEntityField.Children });
                cache.evict({ id: `${CacheEntity.ProductTreeNode}:${productTreeNodeId}`, fieldName: CacheEntityField.Attribute });
            });
            cache.gc();
        },
        onQueryUpdated: (observableQuery) => {
            if (treeNodeIdsToUpdate.includes(observableQuery.variables.nodeId)) {
                return observableQuery.refetch();
            }
            return false;
        },
    }), [client]);
    /**
     *
     * Refetch strategy attributes in selected node and clear cache for others
     * */
    const refetchQueriesOnStrategyAttributeChange = (0, react_1.useCallback)((options) => {
        const { selectedTreeNodeId, relatedTreeNodeIds = [] } = options;
        const allProductTreeNodes = relatedTreeNodeIds.concat(selectedTreeNodeId);
        return client.refetchQueries({
            include: getQueryList([QueryType.StrategyAttributes]),
            updateCache(cache) {
                /*
                  remove productAggregatedLabelsNew field from normalized object ProductTreeNode:id in cache.
                  All related queries will be reloaded
                */
                allProductTreeNodes.forEach((productTreeNodeId) => {
                    cache.evict({ id: `${CacheEntity.ProductTreeNode}:${productTreeNodeId}`, fieldName: CacheEntityField.Attribute });
                });
                // remove all unreachable references
                cache.gc();
            },
            onQueryUpdated(observableQuery) {
                // prevent values and attributes in other nodes to be reloaded
                const { queryName, variables } = observableQuery;
                if (queryName === getOperationName(gql_1.GET_AGGREGATED_TEXT_ATTRIBUTES)
                    && variables.productTreeNodeId === selectedTreeNodeId) {
                    return observableQuery.refetch();
                }
                return false;
            },
        });
    }, [client]);
    // caching enabled for values, so we need to refetch queries in the current node and clear in other related nodes for a specific attribute
    const refetchQueriesOnStrategyValueChange = (0, react_1.useCallback)((options) => {
        const { selectedTreeNodeId, relatedTreeNodeIds = [], attributeName } = options;
        // TODO: prevent the middle section from reloading
        const allProductTreeNodes = relatedTreeNodeIds.concat(selectedTreeNodeId);
        return client.refetchQueries({
            include: getQueryList([QueryType.StrategyAttributeValues]),
            updateCache(cache) {
                // clear cache for all nodes except current one
                allProductTreeNodes.forEach((selectedNodeId) => {
                    // remove values for a specific attribute from cache
                    cache.evict({ id: `${CacheEntity.ProductTreeNode}:${selectedNodeId}`, fieldName: CacheEntityField.Attribute });
                    cache.evict({ id: `${CacheEntity.ProductTreeNode}:${selectedNodeId}`, fieldName: CacheEntityField.StrategyValues });
                });
                cache.gc();
            },
            onQueryUpdated(observableQuery) {
                var _a, _b;
                // prevent values in other nodes to be reloaded
                const { queryName, variables } = observableQuery;
                const { filter, productTreeNodeId } = variables;
                if (productTreeNodeId !== selectedTreeNodeId) {
                    return false;
                }
                const queryAttributeName = (_b = (_a = filter === null || filter === void 0 ? void 0 : filter.label) === null || _a === void 0 ? void 0 : _a.equals) !== null && _b !== void 0 ? _b : '';
                if (queryName === getOperationName(gql_2.GET_AGGREGATED_TEXT_ATTRIBUTE_VALUES)
                    && queryAttributeName === attributeName) {
                    return observableQuery.refetch();
                }
                if (queryName === getOperationName(get_products_query_1.GET_PRODUCTS)) {
                    return observableQuery.refetch();
                }
                return false;
            },
        });
    }, [client]);
    // refetch products and product attributes and evict cache for product values without refetching
    const refetchProductList = (0, react_1.useCallback)((selectedTreeNodeId) => __awaiter(void 0, void 0, void 0, function* () {
        const nodeId = `${CacheEntity.ProductTreeNode}:${selectedTreeNodeId}`;
        // Pre-read cache keys before the batch so we can pass them into updateCache
        // without requiring an InMemoryCache cast inside the callback.
        const cachedNode = client.cache.extract()[nodeId];
        const productPresentationArgs = [];
        // find all cached field names for product presentations with different args
        Object.keys(cachedNode || {})
            .filter((key) => key.startsWith('productPresentationsNew') && key.includes('"first"'))
            .forEach((key) => {
            const argsString = key.substring('productPresentationsNew'.length);
            const argsJson = argsString.slice(1, -1);
            try {
                productPresentationArgs.push(JSON.parse(argsJson));
            }
            catch (e) {
                console.error('Failed to parse args from key:', key, e);
            }
        });
        // Move eviction into updateCache so Apollo coordinates it with the refetch
        // inside a single cache.batch() call. This prevents a race condition where
        // evicting before refetchQueries triggers an automatic cache-and-network
        // re-fetch (Request A) that can race the explicit observableQuery.refetch()
        // (Request B) and overwrite fresh data with stale data when responses
        // arrive out of order.
        yield client.refetchQueries({
            updateCache(cache) {
                productPresentationArgs.forEach((args) => {
                    cache.evict({ id: nodeId, fieldName: 'productPresentationsNew', args });
                });
                cache.gc();
            },
            include: getQueryList([QueryType.Products]),
            onQueryUpdated(observableQuery) {
                if (observableQuery.queryName === getOperationName(get_products_query_1.GET_PRODUCTS)) {
                    return observableQuery.refetch();
                }
                return false;
            },
        });
        return Promise.resolve();
    }), [client]);
    const refetchProductCounters = (0, react_1.useCallback)((options) => {
        const { selectedTreeNodeId, attributeName } = options;
        return client.refetchQueries({
            include: getQueryList([QueryType.ProductCounters]),
            updateCache: updateCache(catalogId, selectedTreeNodeId),
            onQueryUpdated(observableQuery) {
                var _a, _b;
                // prevent values and attributes in other nodes to be reloaded
                const { queryName, variables } = observableQuery;
                const { productTreeNodeId } = variables;
                if (productTreeNodeId !== selectedTreeNodeId) {
                    return false;
                }
                if (queryName === getOperationName(gql_1.GET_AGGREGATED_TEXT_ATTRIBUTES)
                    && variables.filter.isRequirement) {
                    return observableQuery.refetch();
                }
                if (queryName === getOperationName(gql_2.GET_AGGREGATED_TEXT_ATTRIBUTE_VALUES)
                    && (!attributeName || ((_b = (_a = variables.filter) === null || _a === void 0 ? void 0 : _a.label) === null || _b === void 0 ? void 0 : _b.equals) === attributeName)) {
                    return observableQuery.refetch();
                }
                return false;
            },
        });
    }, [client, catalogId]);
    // refetch product details and product attributes and evict cache for product values without refetching
    const refetchProductDetails = (productId) => client.refetchQueries({
        include: getQueryList([QueryType.ProductDetails]),
        onQueryUpdated(observableQuery) {
            // prevent values and attributes in other nodes to be reloaded
            const { queryName, variables } = observableQuery;
            const { productIds } = variables;
            if (productId !== (productIds === null || productIds === void 0 ? void 0 : productIds[0])) {
                return false;
            }
            if (queryName === getOperationName(product_1.GET_PRODUCT_DETAILS)) {
                return observableQuery.refetch();
            }
            return false;
        },
    });
    return {
        refetchProductTree,
        refetchProductTreeNodes,
        refetchQueriesOnStrategyAttributeChange,
        refetchQueriesOnStrategyValueChange,
        refetchProductList,
        refetchProductDetails,
        refetchProductCounters,
    };
};
exports.useRefetchQueries = useRefetchQueries;
